import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Loader2, AlertCircle, ShoppingCart,
  ArrowUpRight, Download, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { supabase } from '@/utils/supabase';

const STATUS_OPTIONS = ['all', 'paid', 'unfulfilled', 'refunded'];

function fmtCurrency(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function StatusBadge({ status }) {
  const map = {
    paid:        'text-emerald-400 bg-emerald-400/10',
    unfulfilled: 'text-amber-400 bg-amber-400/10',
    refunded:    'text-red-400 bg-red-400/10',
    partial:     'text-blue-400 bg-blue-400/10',
    fulfilled:   'text-emerald-400 bg-emerald-400/10',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[0.58rem] font-['Space_Mono'] tracking-[0.08em] uppercase ${map[status] || 'text-[var(--muted)] bg-white/5'}`}>
      {status}
    </span>
  );
}

export default function AdminOrders() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('all');
  const [page, setPage]       = useState(0);
  const [total, setTotal]     = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => { loadOrders(); }, [search, status, page]);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('orders')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (status !== 'all') query = query.eq('status', status);
      if (search) {
        query = query.or(
          `customer_email.ilike.%${search}%,customer_name.ilike.%${search}%,stripe_session_id.ilike.%${search}%`
        );
      }

      const { data, count, error: qErr } = await query;
      if (qErr) throw qErr;
      setOrders(data || []);
      setTotal(count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const header = 'ID,Date,Customer,Email,Phone,Amount,Status\n';
    const rows = orders.map((o) =>
      [
        o.id,
        o.created_at ? new Date(o.created_at).toLocaleDateString() : '',
        `"${o.customer_name || ''}"`,
        o.customer_email || '',
        o.customer_phone || '',
        (o.amount_total / 100).toFixed(2),
        o.status,
      ].join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'orders.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-[var(--ink)]">Orders</h1>
          <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">{total} order{total !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] px-3 py-2 text-[0.62rem] font-['Space_Mono'] tracking-[0.1em] uppercase transition-colors"
        >
          <Download size={11} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by name, email, session ID…"
            className="w-full field-surface pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => { setStatus(s); setPage(0); }}
              className={`px-3 py-2 text-[0.6rem] font-['Space_Mono'] tracking-[0.1em] uppercase transition-colors ${
                status === s
                  ? 'bg-[var(--brand)] text-[#09090b]'
                  : 'border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)]'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
          <AlertCircle size={12} /><span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--paper)] border border-[var(--line)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 size={18} className="animate-spin text-[var(--brand)]" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <ShoppingCart size={28} className="text-[var(--muted)]" strokeWidth={1} />
            <p className="text-[var(--muted)] text-sm font-['Space_Grotesk']">No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--line)]">
                  {['Order', 'Date', 'Customer', 'Amount', 'Status', 'Fulfillment', ''].map((h) => (
                    <th key={h} className="text-left px-5 py-3 font-['Space_Mono'] text-[0.58rem] tracking-[0.12em] uppercase text-[var(--muted)] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-white/3 transition-colors">
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/orders/${order.id}`}
                        className="font-['Space_Mono'] text-[0.65rem] text-[var(--brand)] hover:underline"
                      >
                        #{String(order.id).slice(0, 8).toUpperCase()}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-[var(--muted)] whitespace-nowrap">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div>
                        <p className="text-[var(--ink)]">{order.customer_name || '—'}</p>
                        <p className="text-[var(--muted)] text-[0.6rem] font-['Space_Mono']">{order.customer_email || ''}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-['Space_Mono'] text-[var(--ink)]">
                      {fmtCurrency(order.amount_total || 0)}
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={order.status} /></td>
                    <td className="px-5 py-3">
                      <StatusBadge status={order.fulfillment_status || 'unfulfilled'} />
                    </td>
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/orders/${order.id}`}
                        className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
                      >
                        <ArrowUpRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--muted)] text-xs font-['Space_Mono']">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] transition-colors disabled:opacity-40"
            >
              <ChevronLeft size={13} />
            </button>
            <span className="px-3 text-xs font-['Space_Mono'] text-[var(--muted)]">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-1.5 border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] transition-colors disabled:opacity-40"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
